(() => {
  'use strict';
  if (customElements.get('doi-photo-rotator')) return;
  class DOIPhotoRotator extends HTMLElement {
    connectedCallback() {
      if (this.abort || !window.DOIPhotoDiscovery) return;
      this.abort = new AbortController();
      const on = (target, event, fn) => target?.addEventListener(event, fn, { signal: this.abort.signal });
      this.original = this.querySelector('.doi-opening-photo');
      this.controls = this.querySelector('[data-photo-controls]');
      this.count = this.querySelector('[data-photo-count]');
      this.toggle = this.querySelector('[data-photo-toggle]');
      this.status = this.querySelector('[data-photo-status]');
      this.filterControl = this.querySelector('[data-photo-filter]');
      this.empty = this.querySelector('[data-photo-empty]');
      this.stage = this.querySelector('.doi-photo-stage') || this;
      this.feedNote = this.querySelector('[data-photo-feed-note]');
      this.retry = this.querySelector('[data-photo-retry]');
      let data;
      try {
        data = JSON.parse(this.querySelector('[data-doi-photo-data]').textContent);
        this.editorial = JSON.parse(this.querySelector('[data-doi-photo-editorial]')?.textContent || '{}');
        this.labels = JSON.parse(this.querySelector('[data-doi-photo-labels]')?.textContent || '{}');
      } catch (_) { return; }
      if (!this.original || !data.primary) return;
      this.dataset.photoReady = 'true';
      this.candidates = data.uploads || [];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || this.dataset.autoplay === 'false';
      this.hovered = false;
      this.inView = true;
      this.interval = Math.max(5000, Math.min(15000, Number(this.dataset.interval) || 10000));
      this.limit = Math.max(1, Math.min(30, Number(this.dataset.limit) || 15));
      this.filter = 'all';
      this.pool = [];
      this.items = [];
      this.index = -1;
      this.displayToken = 0;
      this.interacted = false;
      this.boundOpeners = new WeakSet();
      this.originalSource = new URL(data.primary.src, location.origin).pathname.replace(/^\/s\/files\/1\/0868\/6813\/2121/, '/cdn/shop');
      this.cardTemplate = this.original.cloneNode(true);
      this.cardTemplate.querySelectorAll('img, source').forEach(image => { image.removeAttribute('src'); image.removeAttribute('srcset'); });
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');
      this.imageResize = new ResizeObserver(() => this.updateImageSizes());
      this.imageResize.observe(this);
      on(this.querySelector('[data-photo-prev]'), 'click', () => { this.interacted = true; this.paused = true; this.move(-1, true); });
      on(this.querySelector('[data-photo-next]'), 'click', () => { this.interacted = true; this.paused = true; this.move(1, true); });
      on(this.toggle, 'click', () => { this.interacted = true; this.paused = !this.paused; this.sync(); this.preload(); });
      on(this.filterControl, 'change', () => {
        this.interacted = true;
        this.paused = true;
        this.filter = this.filterControl.value;
        this.applyFilter(true);
      });
      on(this.querySelector('[data-photo-show-all]'), 'click', () => {
        this.filterControl.value = 'all';
        this.filter = 'all';
        this.applyFilter(true);
        this.filterControl.focus({ preventScroll: true });
      });
      on(this.retry, 'click', async () => {
        this.pool.forEach(item => { if (item.failed) { item.failed = false; item.promise = null; item.card = null; } });
        await this.discover(true);
        if (this.retry.hidden || this.feedNote.hidden) this.querySelector('[data-photo-open]')?.focus({ preventScroll: true });
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.sync(); } });
      on(this, 'pointerleave', () => { this.hovered = false; this.sync(); this.preload(); });
      on(this, 'focusin', event => { if (event.target !== this.toggle) { this.paused = true; this.sync(); } });
      on(document, 'visibilitychange', () => { this.sync(); if (!document.hidden) this.discover(true); });
      on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.sync(); });
      this.observer = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.sync(); if (this.inView) this.preload(); });
      this.observer.observe(this);
      this.discover();
      this.discoveryTimer = setInterval(() => { if (!document.hidden && this.inView) this.discover(true); }, 60000);
    }

    async discover(fresh = false) {
      if (this.discovering || !this.abort) return;
      this.discovering = true;
      if (this.retry) this.retry.disabled = true;
      try {
        const items = await window.DOIPhotoDiscovery.load(this, this.abort.signal, fresh);
        if (!this.isConnected || !this.abort) return;
        this.discoveryResolved = true;
        await this.refreshItems(items);
      } catch (_) {
        if (!this.isConnected || !this.abort) return;
        this.dataset.photoFeedState = 'fallback';
        this.discoveryResolved = true;
        if (!this.pool.length) await this.refreshItems(this.candidates);
      } finally {
        this.discovering = false;
        if (this.retry) this.retry.disabled = false;
        if (this.isConnected && this.abort) {
          this.updateFeedNote();
          this.sync();
        }
      }
    }

    updateFeedNote() {
      if (!this.feedNote) return;
      const failed = this.dataset.photoFeedState !== 'current' || this.imageFailure;
      this.feedNote.hidden = !failed;
      this.feedNote.querySelector('[data-photo-feed-message]').textContent = this.imageFailure ? this.labels.failed || 'Images could not load. Try again.' : this.labels.cached || 'Showing saved images. Latest uploads are temporarily unavailable.';
    }

    async refreshItems(incoming) {
      const previous = new Map(this.pool.map(item => [item.sourceKey, item]));
      this.pool = window.DOIPhotoDiscovery.curate(incoming, this.editorial, 100).map(item => {
        if (this.labels[item.classification]) item.classificationLabel = this.labels[item.classification];
        if (item.classification === 'unclassified') item.classificationLabel = '';
        const existing = previous.get(item.sourceKey);
        if (!existing) return item;
        Object.assign(existing, item);
        if (existing.card) this.decorate(existing, existing.card);
        return existing;
      }).filter(item => this.eligible(item));
      await this.applyFilter(false);
    }

    eligible(item) {
      return this.dataset.photoScope !== 'through-my-lens' || !['generated', 'illustration', 'reconstruction'].includes(item.classification);
    }

    matches(item) {
      if (!this.eligible(item)) return false;
      return this.filter === 'all' || item.classification === this.filter || this.filter === 'generated' && ['illustration', 'reconstruction'].includes(item.classification);
    }

    async applyFilter(reset) {
      const generation = (this.filterGeneration || 0) + 1;
      this.filterGeneration = generation;
      this.imageFailure = false;
      this.displayToken++;
      this.switching = false;
      const current = this.items[this.index];
      const selected = this.pool.filter(item => this.matches(item)).slice(0, this.limit);
      if (!reset && this.interacted && current && this.matches(current) && !selected.includes(current)) {
        if (selected.length >= this.limit) selected.pop();
        selected.push(current);
      }
      this.items = selected;
      this.index = !reset && current ? selected.indexOf(current) : -1;
      if (this.filterControl) this.filterControl.disabled = false;
      this.empty.hidden = !!selected.length;
      this.empty.querySelector('[data-photo-empty-message]').textContent = this.labels.empty || 'No images of this type among recent uploads.';
      this.controls.hidden = selected.length < 2;
      if (!selected.length) {
        this.querySelectorAll('.doi-opening-photo').forEach(card => { card.hidden = true; });
        this.removeAttribute('data-photo-loading');
        this.status.textContent = this.empty.querySelector('[data-photo-empty-message]').textContent;
        this.sync();
        return;
      }
      if (this.index >= 0 && current.loaded && current.card) {
        this.sync();
        this.preload();
        return;
      }
      this.index = -1;
      for (let index = 0; index < selected.length; index++) {
        if (!selected[index].failed) {
          const shown = await this.show(index, false);
          if (generation !== this.filterGeneration) return;
          if (shown) return;
        }
      }
      this.querySelectorAll('.doi-opening-photo').forEach(card => { card.hidden = true; });
      this.empty.hidden = false;
      this.empty.querySelector('[data-photo-empty-message]').textContent = this.labels.failed || 'Images could not load. Try again.';
      this.feedNote.hidden = false;
      this.imageFailure = true;
      this.removeAttribute('data-photo-loading');
      this.controls.hidden = true;
      this.paused = true;
      this.sync();
    }

    resized(src, width) {
      const url = new URL(src, location.origin);
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      url.searchParams.set('width', width);
      return url.href;
    }

    imageSizes() {
      const width = Math.ceil(this.getBoundingClientRect().width);
      return width > 0 ? `${width}px` : null;
    }

    sourceSet(item) {
      const nativeWidth = Number(item.width);
      const maximum = Number.isFinite(nativeWidth) && nativeWidth > 0 ? Math.min(2048, Math.max(1, Math.round(nativeWidth))) : 2048;
      const widths = [320, 640, 960, 1280, 1600, 2048].filter(width => width < maximum);
      widths.push(maximum);
      return widths.map(width => `${this.resized(item.src, width)} ${width}w`).join(', ');
    }

    updateImageSizes() {
      const sizes = this.imageSizes();
      if (!sizes) return;
      for (const image of this.querySelectorAll('.doi-opening-photo source, .doi-opening-photo img')) {
        if (image.srcset && image.sizes !== sizes) image.sizes = sizes;
      }
    }

    decorate(item, card) {
      const title = card.querySelector('strong');
      const caption = card.querySelector('figcaption p');
      title.textContent = item.title;
      caption.querySelector('span').textContent = item.location;
      const date = caption.querySelector('time');
      date.dateTime = item.uploadedAt;
      date.textContent = 'Added ' + item.dateLabel;
      const badge = card.querySelector('[data-photo-classification]');
      badge.hidden = !item.classificationLabel;
      badge.textContent = item.classificationLabel;
      badge.title = item.classificationDetail || item.classificationLabel;
      card.dataset.classification = item.classification;
      const action = card.querySelector('.doi-opening-photo__link');
      action.hidden = !item.url;
      action.href = item.url || '#';
      action.setAttribute('aria-label', 'Explore story: ' + item.title);
      const opener = card.querySelector('[data-photo-open]');
      opener.href = this.resized(item.src, 2560);
      opener.setAttribute('aria-label', 'View image: ' + item.title + (item.classificationLabel ? '. ' + item.classificationLabel : ''));
      if (!this.boundOpeners.has(opener)) {
        this.boundOpeners.add(opener);
        opener.addEventListener('click', event => {
          event.preventDefault();
          this.interacted = true;
          this.viewPhoto(card._photoItem, opener);
        }, { signal: this.abort.signal });
      }
      card._photoItem = item;
    }

    prepare(item, priority = 'low') {
      if (priority === 'high') {
        this.querySelectorAll('.doi-opening-photo img').forEach(image => { image.fetchPriority = 'low'; });
        const existingImage = item.card?.querySelector('img');
        if (existingImage) existingImage.fetchPriority = 'high';
      }
      if (item.promise) return item.promise;
      const original = item.sourceKey === this.originalSource;
      const card = original ? this.original : this.cardTemplate.cloneNode(true);
      card.hidden = true;
      const title = card.querySelector('strong');
      const caption = card.querySelector('figcaption p');
      if (!original) {
        const key = ++this.cardSequence || (this.cardSequence = 1);
        title.id = 'doi-photo-title-' + key + '-' + this.id;
        caption.id = 'doi-photo-caption-' + key + '-' + this.id;
        card.setAttribute('aria-labelledby', title.id);
        card.setAttribute('aria-describedby', caption.id);
      }
      this.decorate(item, card);
      const image = card.querySelector('img');
      const sizes = this.imageSizes() || '100vw';
      image.sizes = sizes;
      image.alt = item.alt || item.title + (item.classificationLabel ? '. ' + item.classificationLabel : '');
      image.loading = 'eager';
      image.fetchPriority = priority;
      image.decoding = 'async';
      if (!original) {
        image.width = item.width || 2048;
        image.height = item.height || 1150;
        image.srcset = this.sourceSet(item);
        card.querySelectorAll('source').forEach(source => { source.sizes = sizes; source.srcset = this.sourceSet(item); });
      }
      item.card = card;
      item.promise = new Promise(resolve => {
        let timeout;
        let settled = false;
        const finish = loaded => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          image.onload = image.onerror = null;
          item.loaded = loaded;
          item.failed = !loaded;
          if (!loaded) card.remove();
          resolve(loaded);
        };
        image.onload = () => finish(image.naturalWidth > 0);
        image.onerror = () => finish(false);
        timeout = setTimeout(() => finish(false), 15000);
        if (!original) image.src = this.resized(item.src, Math.min(Number(item.width) || 1600, 1600));
        if (!card.isConnected) this.stage.insertBefore(card, this.empty);
        if (image.complete) finish(image.naturalWidth > 0);
      });
      return item.promise;
    }

    viewPhoto(item, launcher) {
      this.viewerLauncher = launcher;
      this.paused = true;
      this.sync();
      if (!this.dialog) {
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'doi-photo-viewer';
        const media = document.createElement('div');
        media.className = 'doi-photo-viewer__media';
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'doi-photo-viewer__close';
        close.setAttribute('aria-label', 'Close photo');
        close.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18"/></svg>';
        close.addEventListener('click', () => this.dialog.close());
        this.dialogImage = document.createElement('img');
        const footer = document.createElement('div');
        footer.className = 'doi-photo-viewer__footer';
        this.dialogText = document.createElement('p');
        this.dialogText.id = `${this.querySelector('strong').id}-viewer-caption`;
        this.dialog.setAttribute('aria-describedby', this.dialogText.id);
        this.dialogStory = document.createElement('a');
        this.dialogStory.className = 'doi-photo-viewer__story';
        this.dialogStory.textContent = 'Explore story';
        media.append(this.dialogImage, close);
        footer.append(this.dialogText, this.dialogStory);
        this.dialog.append(media, footer);
        this.dialog.addEventListener('pointerdown', event => { this.viewerBackdropPressed = event.target === this.dialog; });
        this.dialog.addEventListener('click', event => {
          if (event.target === this.dialog && this.viewerBackdropPressed) this.dialog.close();
          this.viewerBackdropPressed = false;
        });
        this.dialog.addEventListener('close', () => { this.viewerLauncher?.focus({ preventScroll: true }); });
        this.append(this.dialog);
      }
      this.dialog.setAttribute('aria-label', item.title);
      this.dialogImage.src = this.resized(item.src, 2560);
      this.dialogImage.alt = item.alt || item.title;
      this.dialogImage.width = item.width || 2048;
      this.dialogImage.height = item.height || 1150;
      this.dialog.style.setProperty('--photo-ratio', this.dialogImage.width / this.dialogImage.height);
      this.dialogText.textContent = [item.title, item.classificationLabel, item.classificationDetail, 'Added ' + item.dateLabel].filter(Boolean).join(' · ');
      this.dialogStory.hidden = !item.url;
      this.dialogStory.href = item.url || '#';
      this.dialog.showModal();
      this.dialog.querySelector('.doi-photo-viewer__close').focus({ preventScroll: true });
    }

    async show(index, manual) {
      if (!this.items[index]) return false;
      const token = ++this.displayToken;
      this.switching = true;
      clearTimeout(this.timer);
      const item = this.items[index];
      const priority = this.index < 0 || manual ? 'high' : 'low';
      const loaded = await this.prepare(item, priority);
      if (token !== this.displayToken || !this.isConnected || !this.abort) return false;
      this.switching = false;
      if (loaded) {
        this.querySelectorAll('.doi-opening-photo').forEach(card => { card.hidden = card !== item.card; });
        this.index = index;
        this.dataset.currentPhoto = item.title;
        this.removeAttribute('data-photo-loading');
        if (manual) this.status.textContent = 'Image ' + (index + 1) + ' of ' + this.items.length + ': ' + item.title + (item.classificationLabel ? '. ' + item.classificationLabel : '');
        this.prune();
      }
      this.sync();
      this.preload();
      return loaded;
    }

    async move(direction, manual = false) {
      if (this.switching || !this.items.length) return;
      for (let step = 1; step <= this.items.length; step++) {
        const index = (this.index + direction * step + this.items.length) % this.items.length;
        if (!this.items[index].failed && await this.show(index, manual)) return;
      }
      this.paused = true;
      this.sync();
    }

    prune() {
      const keep = new Set([this.items[this.index]?.card, this.items[(this.index + 1) % this.items.length]?.card]);
      for (const card of this.querySelectorAll('.doi-opening-photo')) {
        if (keep.has(card) || card.contains(document.activeElement) || this.dialog?.open && card.contains(this.viewerLauncher)) continue;
        const item = card._photoItem;
        card.remove();
        if (!item) continue;
        item.card = null;
        item.promise = null;
        item.loaded = false;
      }
    }

    preload() {
      clearTimeout(this.prefetchTimer);
      if (navigator.connection?.saveData || this.motion.matches) return;
      this.prefetchTimer = setTimeout(() => {
        if (!this.isConnected || !this.inView || document.hidden || !this.discoveryResolved || this.hasAttribute('data-photo-loading') || this.paused || this.hovered) return;
        const next = this.items[(this.index + 1) % this.items.length];
        if (next && !next.failed && next !== this.items[this.index]) this.prepare(next);
      }, 1000);
    }

    sync() {
      clearTimeout(this.timer);
      this.count.textContent = (this.items.length ? Math.max(0, this.index) + 1 : 0) + ' / ' + this.items.length;
      this.toggle.setAttribute('aria-label', this.paused ? 'Play image rotation' : 'Pause image rotation');
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.paused);
      this.toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.paused);
      const playing = this.items.length > 1 && !this.paused && !this.hovered && this.inView && !document.hidden && !this.switching;
      this.dataset.playing = String(playing);
      if (playing && !this.hasAttribute('data-photo-loading')) this.timer = setTimeout(() => this.move(1), this.interval);
    }

    disconnectedCallback() {
      this.displayToken++;
      clearTimeout(this.timer);
      clearTimeout(this.prefetchTimer);
      clearInterval(this.discoveryTimer);
      this.observer?.disconnect();
      this.imageResize?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  }
  customElements.define('doi-photo-rotator', DOIPhotoRotator);
})();



